import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import type { LatLngExpression, PointExpression } from "leaflet";
import "leaflet/dist/leaflet.css";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { components } from "../../types/api";
import CampaignDetailsCard from "../card/CampaignDetailsCard";
import MetricsTimelineChart, {
  MetricDataPoint,
  MetricType,
} from "../card/MetricsTimelineChart";
import {
  formatCurrency,
  formatDecimal,
  formatNumber,
} from "../../utils/formatUtils";
import { exportMetricsToCSV } from "../../utils/exportUtils";
import { useDispatch } from "react-redux";

type CoordinateSample = components["schemas"]["CoordinateSample"];

const isValidCoordinateSample = (
  coordinate?: CoordinateSample | null
): coordinate is CoordinateSample & {
  latitude: number;
  longitude: number;
} =>
  typeof coordinate?.latitude === "number" &&
  typeof coordinate?.longitude === "number";

interface HivestackMergeCampaignDetailsProps {
  isLoadingMergedCampaign: boolean;
  mergedCampaignId: string | undefined;
  mergedCampaignData:
    | components["schemas"]["MergedCampaignMetrics"]
    | null
    | undefined;
  metricsStartDate?: string;
  metricsEndDate?: string;
  onMetricsStartDateChange?: (value: string) => void;
  onMetricsEndDateChange?: (value: string) => void;
  onClearMetricsDateFilters?: () => void;
}

const HivestackMergeCampaignDetails: React.FC<
  HivestackMergeCampaignDetailsProps
> = ({
  isLoadingMergedCampaign,
  mergedCampaignData,
  metricsStartDate,
  metricsEndDate,
  onMetricsStartDateChange,
  onMetricsEndDateChange,
  onClearMetricsDateFilters,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const campaignStartDate = mergedCampaignData?.startDate
    ? dayjs(mergedCampaignData.startDate).format("YYYY-MM-DD")
    : "";
  const campaignEndDate = mergedCampaignData?.endDate
    ? dayjs(mergedCampaignData.endDate).format("YYYY-MM-DD")
    : "";
  const [internalStartDate, setInternalStartDate] =
    useState<string>(campaignStartDate);
  const [internalEndDate, setInternalEndDate] =
    useState<string>(campaignEndDate);

  const resolvedStartDate = metricsStartDate ?? internalStartDate;
  const resolvedEndDate = metricsEndDate ?? internalEndDate;

  const handleMetricsStartDateChange = (value: string) => {
    if (onMetricsStartDateChange) {
      onMetricsStartDateChange(value);
    } else {
      setInternalStartDate(value);
    }

    if (!value) {
      if (onMetricsEndDateChange) {
        onMetricsEndDateChange("");
      } else {
        setInternalEndDate("");
      }
    }
  };

  const handleMetricsEndDateChange = (value: string) => {
    if (onMetricsEndDateChange) {
      onMetricsEndDateChange(value);
    } else {
      setInternalEndDate(value);
    }
  };

  const handleClearMetricsDates = () => {
    if (onClearMetricsDateFilters) {
      onClearMetricsDateFilters();
    } else {
      setInternalStartDate(campaignStartDate);
      setInternalEndDate(campaignEndDate);
    }
  };

  const summary = mergedCampaignData?.metrics?.summary;
  const rawDailyMetrics = mergedCampaignData?.metrics?.dailyData;
  const dailyMetrics = useMemo(() => rawDailyMetrics ?? [], [rawDailyMetrics]);
  const filteredDailyMetrics = useMemo(() => {
    return dailyMetrics.filter((day) => {
      const currentDate = day.date;
      if (!currentDate) return false;
      if (resolvedStartDate && currentDate < resolvedStartDate) return false;
      if (resolvedEndDate && currentDate > resolvedEndDate) return false;
      return true;
    });
  }, [dailyMetrics, resolvedStartDate, resolvedEndDate]);

  const totalCost = summary?.totalCost ?? 0;
  const totalImpressions = summary?.totalImpressions ?? 0;
  const totalPlays = summary?.plays ?? 0;
  const summaryFrequency =
    totalPlays > 0 ? totalImpressions / totalPlays : null;

  const rawMetrics = useMemo(() => {
    const metricsWithRaw = mergedCampaignData?.metrics as
      | {
          rawMetrics?: components["schemas"]["CampaignMetricsDTO"][];
        }
      | undefined;
    return metricsWithRaw?.rawMetrics ?? [];
  }, [mergedCampaignData?.metrics]);

  const hiddenChartMetrics = useMemo<MetricType[]>(
    () => ["ctr", "cpc", "cpm", "clicks"] as MetricType[],
    []
  );

  const metricLabelsOverride = useMemo(
    () => ({
      cost: t("home.dashboardInvestment", {
        defaultValue: "Investment",
      }),
      completeViews: t("campaigns.plays", { defaultValue: "Plays" }),
    }),
    [t]
  );

  const filteredMapPoints = useMemo(() => {
    const pointsFromDailyMetrics = filteredDailyMetrics.flatMap((day) => {
      if (!day.date) {
        return [];
      }

      const playsValue =
        (day as { plays?: number | null }).plays ?? day.completeViews ?? 0;
      const basePoint = {
        plays: playsValue ?? 0,
        impressions: day.impressions ?? 0,
        cost: day.cost ?? 0,
        date: day.date,
      };

      const coordinateSamples = (day as { coordinates?: CoordinateSample[] })
        .coordinates;
      if (coordinateSamples?.length) {
        const coordinates = coordinateSamples
          .filter(isValidCoordinateSample)
          .map((coordinate) => ({
            latitude: coordinate.latitude,
            longitude: coordinate.longitude,
            ...basePoint,
          }));
        if (coordinates.length) {
          return coordinates;
        }
      }

      const dayLatitude = (day as { latitude?: number | null }).latitude;
      const dayLongitude = (day as { longitude?: number | null }).longitude;

      if (typeof dayLatitude === "number" && typeof dayLongitude === "number") {
        return [
          {
            latitude: dayLatitude,
            longitude: dayLongitude,
            ...basePoint,
          },
        ];
      }

      return [];
    });

    if (pointsFromDailyMetrics.length) {
      return pointsFromDailyMetrics;
    }

    const pointsFromRawMetrics = rawMetrics.flatMap((metric) => {
      if (!metric.date) {
        return [];
      }

      if (resolvedStartDate && metric.date < resolvedStartDate) {
        return [];
      }

      if (resolvedEndDate && metric.date > resolvedEndDate) {
        return [];
      }

      const basePoint = {
        plays: metric.plays ?? metric.completeViews ?? 0,
        impressions: metric.impressions ?? 0,
        cost: metric.cost ?? 0,
        date: metric.date,
      };

      const coordinateSamples = (metric as { coordinates?: CoordinateSample[] })
        .coordinates;
      if (coordinateSamples?.length) {
        const coordinates = coordinateSamples
          .filter(isValidCoordinateSample)
          .map((coordinate) => ({
            latitude: coordinate.latitude,
            longitude: coordinate.longitude,
            ...basePoint,
          }));
        if (coordinates.length) {
          return coordinates;
        }
      }

      if (
        typeof metric.latitude === "number" &&
        typeof metric.longitude === "number"
      ) {
        return [
          {
            latitude: metric.latitude,
            longitude: metric.longitude,
            ...basePoint,
          },
        ];
      }

      return [];
    });

    if (pointsFromRawMetrics.length) {
      return pointsFromRawMetrics;
    }

    return [];
  }, [filteredDailyMetrics, rawMetrics, resolvedStartDate, resolvedEndDate]);

  const mapCenter = useMemo(() => {
    if (!filteredMapPoints.length) {
      return [20, 0] as LatLngExpression;
    }

    const avgLat =
      filteredMapPoints.reduce((sum, point) => sum + point.latitude, 0) /
      filteredMapPoints.length;
    const avgLon =
      filteredMapPoints.reduce((sum, point) => sum + point.longitude, 0) /
      filteredMapPoints.length;
    return [avgLat, avgLon] as LatLngExpression;
  }, [filteredMapPoints]);

  const mapZoom = useMemo(() => {
    if (!filteredMapPoints.length) return 2;
    if (filteredMapPoints.length === 1) return 6;
    if (filteredMapPoints.length < 10) return 4;
    return 3;
  }, [filteredMapPoints]);

  const timelineData: (MetricDataPoint & { plays: number })[] = useMemo(() => {
    return filteredDailyMetrics.map((day) => {
      const playsValue =
        (day as { plays?: number | null }).plays ?? day.completeViews ?? 0;

      return {
        date: day.date,
        ctr: day.ctr ?? 0,
        cpc: day.cpc ?? 0,
        cpm: day.cpm ?? 0,
        impressions: day.impressions ?? 0,
        clicks: day.clicks ?? 0,
        cost: day.cost ?? 0,
        completeViews: day.completeViews ?? 0,
        plays: playsValue ?? 0,
      };
    });
  }, [filteredDailyMetrics]);

  const dailyRows = useMemo(() => {
    return filteredDailyMetrics.map((day) => {
      const playsValue =
        (day as { plays?: number | null }).plays ?? day.completeViews ?? null;
      const impressions = day.impressions ?? 0;
      const frequency =
        playsValue && playsValue > 0 ? impressions / playsValue : null;

      return {
        date: day.date,
        cost: day.cost ?? 0,
        impressions,
        plays: playsValue,
        frequency,
      };
    });
  }, [filteredDailyMetrics]);

  if (isLoadingMergedCampaign) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.grey[200]}`,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          {t("campaigns.noMetricsData", {
            defaultValue: "No metrics available for this campaign.",
          })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t("campaigns.metrics", { defaultValue: "Metrics" })}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              rowGap: 2,
              flexWrap: "wrap",
            }}
          >
            <TextField
              label={t("common.startDate", { defaultValue: "Start Date" })}
              type="date"
              size="small"
              value={resolvedStartDate || ""}
              onChange={(e) => handleMetricsStartDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: campaignStartDate || undefined,
                max: resolvedEndDate || campaignEndDate || undefined,
              }}
              sx={{ width: 150 }}
            />

            <TextField
              label={t("common.endDate", { defaultValue: "End Date" })}
              type="date"
              size="small"
              value={resolvedEndDate || ""}
              onChange={(e) => handleMetricsEndDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: resolvedStartDate || campaignStartDate || undefined,
                max: campaignEndDate || undefined,
              }}
              sx={{ width: 150 }}
              disabled={!resolvedStartDate}
            />

            <IconButton
              onClick={handleClearMetricsDates}
              size="small"
              color="primary"
              title={t("common.clearFilters", {
                defaultValue: "Clear Filters",
              })}
            >
              <ClearIcon />
            </IconButton>

            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={() =>
                exportMetricsToCSV(
                  filteredDailyMetrics.map((day) => ({
                    date: day.date,
                    impressions: day.impressions ?? 0,
                    clicks: day.clicks ?? 0,
                    ctr: day.ctr ?? 0,
                    cpm: day.cpm ?? 0,
                    cpc: day.cpc ?? 0,
                    cost: day.cost ?? 0,
                    cpv: day.cpv ?? 0,
                    vtr: day.vtr ?? 0,
                    dailyInvestment: day.cost ?? 0,
                    dailyImpressions: day.impressions ?? 0,
                    completeViews:
                      (day as { plays?: number | null }).plays ??
                      day.completeViews ??
                      0,
                    dailyCTR: day.ctr ?? 0,
                    conversions: day.conversions ?? 0,
                  })),
                  mergedCampaignData?.name || "hivestack",
                  t,
                  dispatch
                )
              }
              sx={{
                mt: { xs: 1, sm: 0 },
                ml: { xs: 0, sm: 1 },
                height: 36,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              {t("campaigns.exportMetrics", { defaultValue: "Export Data" })}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            "& > *": {
              flex: "1 1 calc(25% - 24px)",
              minWidth: "180px",
            },
          }}
        >
          <CampaignDetailsCard
            value={formatCurrency(totalCost, mergedCampaignData?.currency_enum)}
            label={t("campaigns.cost", { defaultValue: "Cost" })}
          />
          <CampaignDetailsCard
            value={formatNumber(totalImpressions)}
            label={t("campaigns.impressions", { defaultValue: "Impressions" })}
          />
          <CampaignDetailsCard
            value={formatNumber(totalPlays)}
            label={t("campaigns.plays", { defaultValue: "Plays" })}
          />
          <CampaignDetailsCard
            value={
              summaryFrequency !== null
                ? formatDecimal(summaryFrequency)
                : t("common.notAvailable", { defaultValue: "N/A" })
            }
            label={t("campaigns.frequency", { defaultValue: "Frequency" })}
          />
        </Box>

        <Box sx={{ mt: 4 }}>
          <MetricsTimelineChart
            title={t("campaigns.metricsOverTime", {
              defaultValue: "Metrics Over Time",
            })}
            subtitle={t("campaigns.clickMetricsToCompare", {
              defaultValue:
                "Click on metrics to compare or select 'All' to see accumulated data",
            })}
            data={timelineData}
            isLoading={isLoadingMergedCampaign}
            hiddenMetrics={hiddenChartMetrics}
            metricLabelsOverride={metricLabelsOverride}
          />
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {t("campaigns.hivestack.dailyMetrics", {
            defaultValue: "Daily performance",
          })}
        </Typography>

        {dailyRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("campaigns.hivestack.noDailyMetrics", {
              defaultValue: "No daily Hivestack metrics available yet.",
            })}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    {t("common.date", { defaultValue: "Date" })}
                  </TableCell>
                  <TableCell align="right">
                    {t("campaigns.cost", { defaultValue: "Cost" })}
                  </TableCell>
                  <TableCell align="right">
                    {t("campaigns.impressions", {
                      defaultValue: "Impressions",
                    })}
                  </TableCell>
                  <TableCell align="right">
                    {t("campaigns.plays", { defaultValue: "Plays" })}
                  </TableCell>
                  <TableCell align="right">
                    {t("campaigns.frequency", { defaultValue: "Frequency" })}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dailyRows.map((row) => (
                  <TableRow key={`${row.date}-${row.impressions}`}>
                    <TableCell>
                      {dayjs(row.date).format("MMM DD, YYYY")}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(
                        row.cost,
                        mergedCampaignData?.currency_enum
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(row.impressions)}
                    </TableCell>
                    <TableCell align="right">
                      {row.plays !== null && row.plays !== undefined
                        ? formatNumber(row.plays)
                        : t("common.notAvailable", { defaultValue: "N/A" })}
                    </TableCell>
                    <TableCell align="right">
                      {row.frequency !== null
                        ? formatDecimal(row.frequency)
                        : t("common.notAvailable", { defaultValue: "N/A" })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {t("campaigns.hivestack.geoTitle", {
            defaultValue: "Impressions on map",
          })}
        </Typography>

        {!filteredMapPoints.length ? (
          <Typography variant="body2" color="text.secondary">
            {t("campaigns.hivestack.noGeoData", {
              defaultValue: "No metrics with location data for this period.",
            })}
          </Typography>
        ) : (
          <Box sx={{ mt: 2, height: 420, borderRadius: 2, overflow: "hidden" }}>
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              style={{ width: "100%", height: "100%" }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {filteredMapPoints.map((point, index) => {
                const radius = Math.min(
                  14,
                  Math.max(4, Math.log(point.plays + 1) * 2)
                );
                return (
                  <CircleMarker
                    key={`${point.latitude}-${point.longitude}-${index}`}
                    center={
                      [point.latitude, point.longitude] as LatLngExpression
                    }
                    radius={radius}
                    pathOptions={{
                      color: "#1976d2",
                      fillColor: "#64b5f6",
                      fillOpacity: 0.65,
                      weight: 1,
                    }}
                  >
                    <Tooltip
                      direction="top"
                      offset={[0, -radius] as PointExpression}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="body2" fontWeight={600}>
                          {dayjs(point.date).format("MMM DD, YYYY")}
                        </Typography>
                        <Typography variant="caption">
                          {t("campaigns.plays", { defaultValue: "Plays" })}:{" "}
                          {point.plays}
                        </Typography>
                        <Typography variant="caption">
                          {t("campaigns.impressions", {
                            defaultValue: "Impressions",
                          })}
                          : {point.impressions}
                        </Typography>
                        <Typography variant="caption">
                          {t("campaigns.cost", { defaultValue: "Cost" })}:{" "}
                          {formatCurrency(point.cost)}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default HivestackMergeCampaignDetails;
